import { Link, LinkProps } from "@tanstack/react-router"
import { PermutLogo } from "assets/logo"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "design-system/organisms/Sidebar"
import { cn } from "design-system/utils"
import {
  AudioLines,
  BookMarked,
  Bug,
  ChartNoAxesColumn,
  ChartPie,
  Goal,
  Headset,
  HeartHandshake,
  LucideIcon,
  MessageSquareText,
  PencilRuler,
  Settings,
} from "lucide-react"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { SidebarReferralButton } from "modules/referrals/components/SidebarReferralButton"
import { BookDemoButton } from "modules/subscription/components/BookDemoButton"
import { SidebarUsage } from "modules/subscription/components/SidebarUsage"
import "modules/subscription/lang"
import * as React from "react"
import { ComponentType, SVGProps, useState } from "react"
import { useTranslation } from "react-i18next"
import { AdminOnly } from "../AdminOnly"
import { ChangePasswordModal } from "./ChangePasswordModal"
import { NavMain } from "./NavMain"
import { NavUser } from "./NavUser"
import { WorkspaceSelector } from "./WorkspaceSelector"

type BaseNavLink = {
  title: string
  icon: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
  subItems?: NavLink[] // recursively use the main type
  target?: LinkProps["target"]
}

type InternalNavLink = BaseNavLink & {
  url: LinkProps["to"]
  externalUrl?: never
  params?: Record<string, string | number>
}

type ExternalNavLink = BaseNavLink & {
  externalUrl: string
  url?: never
}

export type NavLink = InternalNavLink | ExternalNavLink

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation()
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState<boolean>(false)
  const { isMobile, setOpenMobile, state } = useSidebar()
  const isAmphoraCompany = useIsAmphoraCompany()

  const navMain = [
    {
      title: t("shared.nav.insights"),
      url: "/insights",
      icon: ChartNoAxesColumn,
    },
    {
      title: t("shared.nav.campaigns"),
      url: "/campaigns",
      icon: AudioLines,
      subItems: [
        {
          title: t("shared.nav.goals"),
          url: "/campaigns/goals",
          icon: Goal,
        },
      ],
    },
    {
      title: t("shared.nav.conversations"),
      url: "/conversations",
      icon: MessageSquareText,
    },
    !isAmphoraCompany && {
      title: t("shared.nav.customers"),
      url: "/customers",
      icon: HeartHandshake,
      subItems: [
        {
          title: t("shared.nav.segments"),
          url: "/customers/segments",
          icon: ChartPie,
        },
      ],
    },
    {
      title: t("shared.nav.support"),
      url: "/support",
      icon: Headset,
    },
    {
      title: t("shared.nav.knowledge"),
      url: "/knowledge",
      icon: BookMarked,
    },
    {
      title: t("shared.nav.capabilities"),
      url: "/capabilities",
      icon: PencilRuler,
    },
    {
      title: t("shared.nav.settings"),
      url: "/settings",
      icon: Settings,
    },
  ].filter(Boolean) as NavLink[]

  const backofficeNav: NavLink[] = [
    {
      title: t("backoffice.header.title"),
      url: "/backoffice",
      icon: Bug,
    },
  ]

  const isCollapsed = state === "collapsed"

  const closeMobileSidebar = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  return (
    <>
      <Sidebar {...props}>
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                className="data-[slot=sidebar-menu-button]:p-1.5! h-auto bg-transparent hover:bg-transparent active:bg-transparent"
              >
                <Link to="/" className="mb-4" onClick={closeMobileSidebar}>
                  <div className="flex flex-row justify-start items-center gap-4 h-14 w-full">
                    <PermutLogo
                      square={isCollapsed}
                      className={cn("w-30", isCollapsed && "w-12")}
                    />
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <WorkspaceSelector />
          <NavMain items={navMain} />
          <div className="flex flex-col gap-0 mt-auto">
            <AdminOnly>
              <NavMain items={backofficeNav} />
            </AdminOnly>
            <BookDemoButton />
            <SidebarReferralButton />
            <SidebarUsage />
          </div>
        </SidebarContent>
        <SidebarFooter>
          <NavUser setIsChangePasswordModalOpen={setIsChangePasswordModalOpen} />
        </SidebarFooter>
      </Sidebar>
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        setIsOpen={setIsChangePasswordModalOpen}
      />
    </>
  )
}
